import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { aggregateReactions, isReactionRow, sameEmoji } from "./reactions";

// Separa as linhas de reação das mensagens que viram bolha e cuida do envio.
const useReactions = (messagesList, resetKey) => {
  // Reação enviada daqui aparece na hora; a linha de verdade chega depois pelo socket e,
  // por ser mais nova, passa a valer no lugar desta.
  const [localRows, setLocalRows] = useState([]);
  const reactionsRef = useRef(new Map());

  useEffect(() => {
    setLocalRows([]);
  }, [resetKey]);

  const { rows, visibleMessages } = useMemo(() => {
    const reactionRows = [];
    const visible = [];
    messagesList.forEach((m) => (isReactionRow(m) ? reactionRows : visible).push(m));
    return { rows: reactionRows, visibleMessages: visible };
  }, [messagesList]);

  const reactionsById = useMemo(
    () => aggregateReactions([...rows, ...localRows], visibleMessages),
    [rows, localRows, visibleMessages]
  );
  reactionsRef.current = reactionsById;

  // Mesmo emoji de novo = tirar a reação (como no WhatsApp).
  const reactTo = useCallback(async (message, emoji) => {
    if (!message || message.id == null) return;
    const current = reactionsRef.current.get(message.id);
    const next = emoji && current && current.mine && sameEmoji(current.mine, emoji) ? "" : emoji || "";
    try {
      await api.post(`/messages/${message.id}/reactions`, { emoji: next });
      setLocalRows((prev) => [
        ...prev,
        {
          id: `local-reaction-${Date.now()}-${prev.length}`,
          mediaType: "reactionMessage",
          body: next,
          fromMe: true,
          quotedMsgId: message.id,
          createdAt: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      const status = err && err.response && err.response.status;
      const code = err && err.response && err.response.data && err.response.data.error;
      if (code && String(code).startsWith("ERR_REACTION_") && i18n.exists(`backendErrors.${code}`)) {
        toast.info(i18n.t(`backendErrors.${code}`), { toastId: code });
      } else if (!status || status === 400 || status === 404) {
        // servidor ainda sem a rota de reações, ou mensagem que o WhatsApp não deixa reagir
        toast.info(i18n.t("conversation.reactions.unavailable"), { toastId: "reaction-unavailable" });
      } else {
        toastError(err);
      }
    }
  }, []);

  return { visibleMessages, reactionsById, reactTo };
};

export default useReactions;
